import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux/useApp';
import { QueueFilters, QueueListItem } from '../models';
import { QUEUE_POLL_INTERVAL_MS } from '../models/lock';
import { fetchQueueThunk, loadMoreQueueThunk } from '../store/contentModerationThunks';
import {
  selectQueueError,
  selectQueueHasMore,
  selectQueueIsAppending,
  selectQueueIsLoading,
  selectQueueIsPolling,
  selectQueueItems,
  selectQueueLastFetchedAt,
  selectQueuePage,
  selectQueuePageSize,
  selectQueueTotalCount,
} from '../store/contentModerationSelectors';

export interface UseReviewQueueResult {
  items: QueueListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  isLoading: boolean;
  isPolling: boolean;
  isAppending: boolean;
  error: ReturnType<typeof selectQueueError>;
  lastFetchedAt: number | null;
  secondsUntilRefresh: number;
  refresh: () => void;
  loadMore: () => void;
}

export interface UseReviewQueueOptions {
  pollIntervalMs?: number;
  /** Skip the visibility check (useful in tests). */
  ignoreVisibility?: boolean;
}

function isDocumentVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return !document.hidden;
}

export function useReviewQueue(
  filters: QueueFilters,
  options: UseReviewQueueOptions = {},
): UseReviewQueueResult {
  const dispatch = useAppDispatch();
  const items = useAppSelector(selectQueueItems);
  const totalCount = useAppSelector(selectQueueTotalCount);
  const page = useAppSelector(selectQueuePage);
  const pageSize = useAppSelector(selectQueuePageSize);
  const hasMore = useAppSelector(selectQueueHasMore);
  const isLoading = useAppSelector(selectQueueIsLoading);
  const isPolling = useAppSelector(selectQueueIsPolling);
  const isAppending = useAppSelector(selectQueueIsAppending);
  const error = useAppSelector(selectQueueError);
  const lastFetchedAt = useAppSelector(selectQueueLastFetchedAt);
  // The Load-more callback closes over the last echoed page so it always
  // requests the next one. Reading from Redux (not the filters prop) means
  // it stays correct even when the user has paginated past page 1.
  const pageRef = useRef(page);
  pageRef.current = page;
  const isAppendingRef = useRef(isAppending);
  isAppendingRef.current = isAppending;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  const pollIntervalMs = options.pollIntervalMs ?? QUEUE_POLL_INTERVAL_MS;
  const ignoreVisibility = options.ignoreVisibility ?? false;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(() =>
    Math.floor(pollIntervalMs / 1000),
  );

  const fireFetch = useCallback(
    (isPoll: boolean) => {
      void dispatch(fetchQueueThunk(filtersRef.current, { isPoll }));
    },
    [dispatch],
  );

  // Initial + filter-change fetch.
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    fireFetch(false);
  }, [filtersKey, fireFetch]);

  // Reset the countdown whenever a successful fetch lands.
  useEffect(() => {
    if (lastFetchedAt != null) {
      setSecondsUntilRefresh(Math.floor(pollIntervalMs / 1000));
    }
  }, [lastFetchedAt, pollIntervalMs]);

  // Poll interval — fires the fetch at the cadence, pauses while hidden.
  useEffect(() => {
    if (pollIntervalMs <= 0) return undefined;

    const tick = () => {
      if (ignoreVisibility || isDocumentVisible()) {
        fireFetch(true);
      }
    };

    const intervalId = window.setInterval(tick, pollIntervalMs);

    const onVisibility = () => {
      if (isDocumentVisible()) fireFetch(true);
    };

    if (!ignoreVisibility && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      window.clearInterval(intervalId);
      if (!ignoreVisibility && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [pollIntervalMs, ignoreVisibility, fireFetch]);

  // 1-second countdown so the header can render a live "X:XX" timer.
  // Separate from the poll interval so the display stays smooth even when the
  // tab is briefly hidden (we just stop decrementing).
  useEffect(() => {
    if (pollIntervalMs <= 0) return undefined;

    const intervalId = window.setInterval(() => {
      if (ignoreVisibility || isDocumentVisible()) {
        setSecondsUntilRefresh((prev) => Math.max(0, prev - 1));
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [pollIntervalMs, ignoreVisibility]);

  const refresh = useCallback(() => fireFetch(false), [fireFetch]);

  const loadMore = useCallback(() => {
    // Guard rails: ignore clicks while a load is in flight (prevents
    // double-firing) and when the server has nothing more to give (the
    // button is normally hidden in that case, but a programmatic call or
    // a click that lands during the response would otherwise over-fetch).
    if (isAppendingRef.current || !hasMoreRef.current) return;
    const nextPage = pageRef.current + 1;
    void dispatch(
      loadMoreQueueThunk({ ...filtersRef.current, page: nextPage }),
    );
  }, [dispatch]);

  return {
    items,
    totalCount,
    page,
    pageSize,
    hasMore,
    isLoading,
    isPolling,
    isAppending,
    error,
    lastFetchedAt,
    secondsUntilRefresh,
    refresh,
    loadMore,
  };
}
